import { useRequest, useSize } from 'ahooks'
import { Divider, Popover } from 'antd'
import { mapValues, uniq } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { UserAvatar } from '@/components'
import { MosaicComponent, getPointPermission } from '@/components'
import { PAGE_RESOURCE_POINTS_ENUM } from '@/schemas'

import { Production, ProductionFLow, ProductionFlowStatus } from '../../production.schema'

export const FlowStatusChart: React.FC<{ production: Production }> = ({ production }) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const { t, i18n } = useTranslation()
  const language = i18n.language
  const isENAndSmallScreen = language === 'en'
  const { canRead } = getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_成员, production.permissions)

  const flows = production.flows || []
  const ProjectCancelProcessIndex = 0 as const // 项目取消流程
  const ProjectMainProcessIndex = 1 as const // 项目主流程
  const ProjectRejectProcessIndex = 2 as const // 项目拒绝流程
  const HorizontalStageCount = 9 as const // 水平划分
  const VerticalStageCount = 3 as const // 垂直划分

  const size = useSize(ref)

  const { data } = useRequest<{
    [x: string]: { roles: string[]; users: string[][] }
  }>(
    () => {
      return {
        params: { selfId: production.selfId },
        url: '/api/production/flow/action/roles',
        method: 'get',
      }
    },
    { manual: !canRead }
  )

  const [flowStatusStageParams, setFlowStatusStageParams] = React.useState<{
    [x: string]: {
      verticalIndex: number
      horizontalIndex: number
      title: string
      validStatus: ProductionFlowStatus[]
      roleUsers?: {
        role: string
        users: string[]
      }[]
    }
  }>({
    立项申请: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 0,
      title: t('production.flowStatus.ProjectWaitForApply.button'),
      validStatus: [ProductionFlowStatus.待指派TO],
    },
    指派TO: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 1,
      title: t('production.flowStatus.TOWaitForAssign.button'),
      validStatus: [ProductionFlowStatus.待资源预估],
    },
    资源预估: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 2,
      title: t('production.flowStatus.ResourceWaitForEstimate.button'),
      validStatus: [ProductionFlowStatus.待立项审批],
    },
    立项通过: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 3,
      title: t('production.flowStatus.ProjectWaitForAudit.pass.button'),
      validStatus: [ProductionFlowStatus.待IPD审批],
    },
    IPD审批: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 4,
      title: t('production.flowStatus.IPDWaitForAudit.chart'),
      validStatus: [ProductionFlowStatus.研发进行中],
    },
    TO确认: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 5,
      title: t('production.flowStatus.DevOnGoing.TOConfirm.button'),
      validStatus: [ProductionFlowStatus.研发进行中],
    },
    研发交付: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 6,
      title: t('production.flowStatus.DevOnGoing.delivery.button'),
      validStatus: [ProductionFlowStatus.待产品验收],
    },
    产品验收: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 7,
      title: t('production.flowStatus.ProductWaitForValidate.chart'),
      validStatus: [ProductionFlowStatus.待项目结项],
    },
    项目结项: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 8,
      title: t('production.flowStatus.ProjectWaitForFinish.button'),
      validStatus: [ProductionFlowStatus.项目已结项],
    },
    项目关闭: {
      verticalIndex: ProjectCancelProcessIndex,
      horizontalIndex: 8,
      title: t('production.flowStatus.ProjectClosed.chart'),
      validStatus: [ProductionFlowStatus.项目已关闭],
    },
    项目驳回: {
      verticalIndex: ProjectRejectProcessIndex,
      horizontalIndex: 8,
      title: t('production.flowStatus.ProjectRejected.chart'),
      validStatus: [ProductionFlowStatus.项目已驳回],
    },
  })

  React.useEffect(() => {
    if (data) {
      const newFlowStatusStageParams = mapValues(flowStatusStageParams, (v, key) => {
        const roleUsers = (data[key].roles || []).map((r, index) => {
          const users = data[key].users[index]
          let title = t(`production.${r}`)
          if (r === 'IPDCommittee') {
            title = t('production.ipdCommittee')
          }
          if (r === 'RDVP') {
            title = t('production.rdvp')
          }

          if (r === 'PMVP') {
            title = t('production.pmvp')
          }

          if (r === 'FinancialOperation') {
            title = t('production.financialOperation')
          }

          let validFlows = [] as ProductionFLow[]
          validFlows = flows.filter((f) => v.validStatus.includes(f.status))

          if (key === '立项通过') {
            const hasIPDAudit = flows.find((f) => f.status === ProductionFlowStatus.待IPD审批)
            const hasDevOnGoing = flows.find((f) => f.status === ProductionFlowStatus.研发进行中)
            // 有IPD则寻找IPD状态
            if (hasIPDAudit) {
              validFlows = flows.filter((f) => f.status === ProductionFlowStatus.待IPD审批)
              // 找第一次研发进行中状态
            } else if (hasDevOnGoing) {
              validFlows = flows
                .filter((f) => f.status === ProductionFlowStatus.研发进行中)
                .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                .slice(0, 1)
            } else {
              validFlows = []
            }
          }

          if (key === 'IPD审批') {
            const hasIPDAudit = flows.find((f) => f.status === ProductionFlowStatus.待IPD审批)
            const hasDevOnGoing = flows.find((f) => f.status === ProductionFlowStatus.研发进行中)
            // 如果 flows 没有 IPD 审批节点 或研发进行中节点，则还没有进行IPD审批
            if (!hasIPDAudit || !hasDevOnGoing) {
              validFlows = []
            } else {
              // 找第一次研发进行中状态
              validFlows = flows
                .filter((f) => f.status === ProductionFlowStatus.研发进行中)
                .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                .slice(0, 1)
            }
          }

          if (key === 'TO确认') {
            const hasDevOnGoing = flows.find((f) => f.status === ProductionFlowStatus.研发进行中)
            if (hasDevOnGoing) {
              validFlows = flows.filter(
                (f) =>
                  f.status === ProductionFlowStatus.研发进行中 &&
                  f.additions.find((a) => a.type === 'confirmedDeliveryTime')
              )
            } else {
              validFlows = []
            }
          }

          return {
            role: title,
            users: uniq(validFlows.length > 0 ? validFlows.filter((v) => v.email).map((v) => v.email!) : users),
          }
        })

        return {
          ...v,
          roleUsers,
        }
      })
      setFlowStatusStageParams(newFlowStatusStageParams)
    }
  }, [data])

  const CircleR = 10 as const
  const subComponents = React.useMemo(() => {
    const shapes = Object.keys(flowStatusStageParams).map((key: string) => {
      const shape = flowStatusStageParams[key as keyof typeof flowStatusStageParams]
      const validStatus = shape.validStatus

      if (!size.width || !size.height) {
        return null
      }

      // 空出最后一列文案空间
      const svgRightAreaSpace = isENAndSmallScreen ? 80 : 60
      const svgHeight = size.height
      const svgWidth = size.width - svgRightAreaSpace
      const strokeWidth = 2
      // 第一个状态前线长度占 水平 block 占比
      const prevPaddingRadio = 0.6
      // 水平 block 大小
      const horizontalBlock = Math.floor(svgWidth / HorizontalStageCount)
      // 垂直 block 大小
      const verticalBlock = Math.floor(svgHeight / VerticalStageCount)
      // 状态圆心坐标
      const cx = Math.floor(horizontalBlock * (shape.horizontalIndex + prevPaddingRadio))
      const cy = Math.floor(verticalBlock * (shape.verticalIndex + 0.6))

      const isFlowEnabled = flows.find((f) => {
        const isBaseValid = validStatus.includes(f.status)
        let isSpecificValid = true
        switch (key) {
          case 'TO确认': {
            isSpecificValid = f.additions.some((a) => a.type === 'confirmedDeliveryTime')
            break
          }

          case 'IPD审批': {
            isSpecificValid = f.additions.some((a) => a.type === 'reviewComment')
            break
          }

          case '研发交付': {
            isSpecificValid = production.flowStatus !== ProductionFlowStatus.研发进行中
            break
          }
        }

        return isBaseValid && isSpecificValid
      })
      const circleParam = {
        cx,
        cy,
        r: CircleR,
        fill: isFlowEnabled ? (['项目驳回', '项目关闭'].includes(key as any) ? 'red' : 'green') : 'transparent',
        stroke: 'black',
      }

      // 状态前的直线
      let prevLineParam = null
      switch (key as keyof typeof flowStatusStageParams) {
        case '立项申请': {
          prevLineParam = [
            {
              x1: Math.floor(horizontalBlock * shape.horizontalIndex),
              x2: Math.floor(horizontalBlock * (shape.horizontalIndex + prevPaddingRadio) - CircleR),
              y1: cy,
              y2: cy,
              stroke: isFlowEnabled ? 'green' : 'black',
            },
          ]
          break
        }
        case '项目关闭':
        case '项目驳回': {
          break
        }
        default: {
          prevLineParam = [
            {
              x1:
                Math.floor(horizontalBlock * (shape.horizontalIndex - 1 + prevPaddingRadio)) +
                CircleR +
                +strokeWidth / 2,
              x2: Math.floor(horizontalBlock * (shape.horizontalIndex + prevPaddingRadio)) - CircleR,
              y1: cy,
              y2: cy,
              stroke: isFlowEnabled ? 'green' : 'black',
            },
          ]
        }
      }

      const roleUsers = shape.roleUsers || []
      return (
        <g key={key}>
          {prevLineParam
            ? prevLineParam.map((line, index) => <line key={index} strokeWidth={strokeWidth} {...line}></line>)
            : null}
          <Popover
            content={
              <div>
                {canRead ? (
                  roleUsers.length === 0 ? (
                    <span className='text-[#8A8A9A]'>{t('production.flowChart.role.none')}</span>
                  ) : (
                    roleUsers.map((r, index) => {
                      return (
                        <React.Fragment key={r.role}>
                          {index !== 0 ? <Divider /> : null}
                          <div>
                            <span className='font-medium'>{r.role}</span>
                            {(r.users || []).length === 0 ? (
                              <span className='block text-[#8A8A9A]'>{t('production.flowChart.user.none')}</span>
                            ) : (
                              r.users.map((u, index) => {
                                return (
                                  <div className='my-1' key={index}>
                                    <UserAvatar size={20} email={u} showName />
                                  </div>
                                )
                              })
                            )}
                          </div>
                        </React.Fragment>
                      )
                    })
                  )
                ) : (
                  <MosaicComponent />
                )}
              </div>
            }
          >
            <circle className='cursor-pointer' {...circleParam} strokeWidth={strokeWidth}></circle>
          </Popover>
          <text
            fontSize={12}
            x={['项目关闭', '项目结项', '项目驳回'].includes(key as any) ? cx + 1.5 * CircleR : cx - 2.5 * CircleR}
            y={
              ['项目关闭', '项目结项', '项目驳回'].includes(key as any)
                ? cy + CircleR / 2
                : ['立项申请', '资源预估', '产品验收'].includes(key as any) && isENAndSmallScreen
                  ? cy - 3 * CircleR
                  : cy + 3 * CircleR
            }
          >
            {shape.title}
          </text>
        </g>
      )
    })
    return shapes
  }, [size, production.flowStatus, flowStatusStageParams])

  if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_状态, production.permissions).canRead) {
    return <MosaicComponent />
  }

  return (
    <div ref={ref}>
      <svg className='w-full' height={160}>
        {subComponents}
      </svg>
    </div>
  )
}
